import { isLiveRunOut } from './liveRunBoards';
import {
  formatWinSummary,
  getRunResultsFromPayouts,
  RunPayout,
  RunResult,
  WinAward
} from './winSummary';

/**
 * The live hand's summaries: what the viewer won in the whole hand, and each
 * paid run's result line — for every viewer, observers and guests included.
 */
export interface LiveWinSummaries {
  hand?: string;
  byRun: Record<number, RunResult>;
}

/** The felt's pot labels: "MP"/"Pot" for the main pot, "SP(n)" for a side pot. */
const potNameFromLabel = (label: unknown) => {
  const side = /^SP\((\d+)\)$/.exec(String(label ?? ''));
  return side ? `Side Pot ${side[1]}` : 'Main Pot';
};

/**
 * Which pot a run's settlement sentence names — "wins the main pot",
 * "wins side pot 2", "wins the low half of the side pot", Omaha's "takes the
 * Side Pot(1) of …", the last board's "wins the undistributed pot" — and
 * whether it was the low half. Only the pot is read from the text; who won
 * comes from the row's seat.
 */
const potFromSentence = (sentence: unknown): Pick<WinAward, 'potName' | 'handType'> => {
  const text = String(sentence ?? '');
  // "a share of the low half": a half two players tied for
  // (shared/games/common/winnerMessages.js).
  const match =
    /\b(?:wins|takes) (?:a share of )?(?:the )?(?:(high|low) half of the )?(main pot|undistributed pot|side pot\s*(?:\((\d+)\)|(\d+))?)/i.exec(
      text
    );
  if (!match) return { potName: 'Main Pot' };
  const side = /^side/i.test(match[2]) ? match[3] ?? match[4] ?? '1' : undefined;
  const low = match[1]?.toLowerCase() === 'low' || /\bthe low hand\b/i.test(text);
  const high = match[1]?.toLowerCase() === 'high' || /\bfor the high hand\b/i.test(text);
  return {
    // The remainder is its own pot, as completed history records it
    // (REMAINDER_POT_LABEL), and a Hi-Lo remainder is split into halves.
    potName: /^undistributed/i.test(match[2])
      ? 'Undistributed Pot'
      : side
      ? `Side Pot ${side}`
      : 'Main Pot',
    ...(low ? { handType: 'Lo' as const } : high ? { handType: 'Hi' as const } : {})
  };
};

const parseRunWinners = (value: unknown): Record<string, any> => {
  if (value && typeof value === 'object') return value as Record<string, any>;
  try {
    const parsed = JSON.parse(String(value || '{}'));
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

/** The name a `Payout n — <name> wins …` row starts with, when no detail names the seat. */
const nameFromSentence = (sentence: unknown) =>
  /^Payout \d+ — (.+?) (?:wins|takes) /.exec(String(sentence ?? ''))?.[1];

/**
 * Every payout of every settled run of a live run-out, from each run's durable
 * record (`runWinners.runN`, seat-keyed): its `history` rows, one a pot or half,
 * else its per-seat `details`. Names come from the record's details (the
 * engine's display name for the seat), never the other way round.
 *
 * `details` names no pot or half: one figure per seat, the high and low shares
 * summed on Hi-Lo. It is read as a main-pot win only on a single-pot, high-only
 * hand, as the capture's builder reads it; otherwise that run is left unlabelled,
 * like a run not yet settled, and the other runs keep their payouts.
 */
const getLiveRunPayouts = (game: any): RunPayout[] => {
  const runWinners = parseRunWinners(game.runWinners);
  const total = Number(String(game.runs).split(',')[0]);
  const pots = runWinners.pots;
  const detailsAttributable =
    !/hilo/i.test(String(game.gameType ?? '')) && !(Array.isArray(pots) && pots.length > 1);
  return Array.from({ length: total }, (_, index) => index + 1).flatMap((run) => {
    const record = runWinners[`run${run}`] ?? {};
    const history = Array.isArray(record.history) ? record.history : [];
    const details = Array.isArray(record.details) ? record.details : [];
    if (history.length === 0 && !detailsAttributable) return [];
    const rows = history.length > 0 ? history : details;
    const nameOf = (row: any) =>
      details.find((detail: any) => Number(detail?.seat) === Number(row?.seat))?.name ??
      row?.name ??
      nameFromSentence(row?.msg) ??
      `Seat ${row?.seat}`;
    return rows
      .filter((row: any) => Number(row?.seat) > 0 && Number(row?.win) > 0)
      .map((row: any) => ({
        ...potFromSentence(row?.msg),
        run,
        seat: Number(row.seat),
        name: String(nameOf(row)),
        amount: Number(row.win)
      }));
  });
};

/**
 * What the seat has taken so far in the live hand. On a run-out each settled
 * run's durable record (`runWinners.runN`, seat-keyed); on a single board the
 * felt's paid pots (`pots[].winners`) once the hand reaches the payout.
 */
export const getLiveSeatAwards = (game: any, seat?: number): WinAward[] => {
  if (!seat || seat <= 0 || !game) return [];

  if (isLiveRunOut(game.runs)) {
    return getLiveRunPayouts(game)
      .filter((payout) => payout.seat === seat)
      .map(({ potName, handType, run }) => ({ potName, handType, run }));
  }

  // Negated so an unknown stage (NaN) counts as before the payout.
  if (!(Number(game.hand) >= 14) || !Array.isArray(game.pots)) return [];
  return game.pots.flatMap((pot: any) =>
    (Array.isArray(pot?.winners) ? pot.winners : [])
      .filter((winner: any) => Number(winner?.player) === seat && Number(winner?.win) > 0)
      .map((winner: any) => ({
        potName: potNameFromLabel(pot.label),
        ...(winner.handType === 'Lo' || winner.handType === 'Hi'
          ? { handType: winner.handType }
          : {})
      }))
  );
};

const countLiveSidePots = (game: any) => {
  if (isLiveRunOut(game?.runs)) {
    const pots = parseRunWinners(game.runWinners).pots;
    return Array.isArray(pots) ? Math.max(0, pots.length - 1) : 0;
  }
  return (Array.isArray(game?.pots) ? game.pots : []).filter((pot: any) =>
    /^SP\(\d+\)$/.test(String(pot?.label ?? ''))
  ).length;
};

/**
 * The viewer's live summaries. `seat` is the table-scoped seat the reducer
 * resolved from the viewer's playerGUID (`currentPlayer.seatId`), never a name.
 */
export const getLiveWinSummaries = (game: any, seat?: number): LiveWinSummaries => {
  const options = {
    sidePotCount: countLiveSidePots(game),
    hiLo: /hilo/i.test(String(game?.gameType ?? ''))
  };
  return {
    hand: formatWinSummary(getLiveSeatAwards(game, seat), options),
    byRun:
      game && isLiveRunOut(game.runs)
        ? getRunResultsFromPayouts(getLiveRunPayouts(game), seat, options)
        : {}
  };
};
